'use client';
// Ce que veulent dire les sources et les niveaux de gravité d'une NC, sous le champ
// où l'on choisit. Les niveaux sont LUS de la règle RG-NC-04 servie par le moteur
// (`regles`) : une seule rédaction, jamais recopiée ici.
import React from 'react';
import { useGed } from '@/lib/store';
import { NC_SOURCES } from '@/lib/meta';
import { Bouton } from '@/shared/Bouton';
import { gravitePropose } from '@/lib/proposition-nc';

const note: React.CSSProperties = { margin: '5px 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' };

export function ExplicationSource({ valeur }: { valeur: string }) {
  const aide = (NC_SOURCES.find((s) => s.key === valeur) as { aide?: string } | undefined)?.aide;
  return aide ? <p style={note}>{aide}</p> : null;
}

/**
 * ⚠️ LA PROPOSITION DE GRAVITÉ SE CLIQUE — elle ne s'applique pas toute seule
 * (arbitrage de la RQ, 11/10/2026).
 *
 * Jusqu'au 11/10, cet encart ANNONÇAIT un relèvement que le serveur allait
 * faire de toute façon : « cette NC sera enregistrée en majeure au minimum ».
 * La phrase était honnête, et le résultat ne l'était pas — 9 gravités sur 17
 * en base n'avaient été choisies par personne, et la colonne qu'on lit en revue
 * de direction penchait vers le haut sans que personne l'ait voulu.
 *
 * `onAppliquer` est donc OBLIGATOIRE au sens métier : sans lui, l'encart
 * affiche la raison mais ne propose aucun geste — ce qui est exactement ce
 * qu'il faut sur un formulaire en LECTURE (fiche instruite), et exactement ce
 * qu'il ne faut pas sur un formulaire de saisie. Les deux appelants le passent.
 */
export function ExplicationNiveaux({ impact, gravite, onAppliquer }: {
  impact?: string; gravite?: string;
  /** Appelé avec la gravité proposée quand la personne l'accepte. */
  onAppliquer?: (gravite: string) => void;
}) {
  const { regles } = useGed();
  const r = (regles || []).find((x) => x.ancre === 'nc.gravite');
  if (!r) return null;
  /* ⚠️ LA RÈGLE EST IMPORTÉE, PAS RETAPÉE. `gravitePropose` est le miroir
     surveillé de `backend/src/nc-declaration.ts` : une condition réécrite ici
     ferait annoncer à l'encart autre chose que ce que le serveur fera, et c'est
     précisément le défaut que le miroir existe pour rendre impossible.
     Une gravité vide vaut « mineure », comme côté moteur. */
  const propose = gravitePropose(gravite || 'mineure', impact);
  return (
    <div style={{ marginTop: 6 }}>
      {/* La CONSIGNE quand le moteur la sert (08/10/2026 : « trop de texte »),
          les quatre points de la règle à défaut — le « i » voisin garde la
          règle complète. */}
      {r.consigne ? (
        <p style={{ ...note, marginTop: 0 }}>{r.consigne}</p>
      ) : (
        <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
          {r.regle.map((l) => <li key={l}>{l}</li>)}
        </ul>
      )}
      {propose && (
        <div style={{ marginTop: 7, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
          background: 'var(--amber-50, #FFF8EC)', borderRadius: 'var(--radius-md)', padding: '9px 12px' }}>
          {/* ⚠️ LA RAISON VIENT DU MODULE, elle n'est pas rédigée ici : c'est la
              même phrase que le moteur renvoie dans `propositions`, donc la
              fiche et le formulaire ne peuvent pas se contredire. */}
          <p style={{ ...note, marginTop: 0, color: 'var(--amber-700, #8A5B00)', flex: '1 1 260px' }}>
            <strong>Gravité « {propose.propose} » proposée</strong> — {propose.raison}
          </p>
          {onAppliquer && (
            <Bouton taille="sm" onClick={() => onAppliquer(propose.propose)} style={{ flex: 'none' }}>
              Passer en « {propose.propose} »
            </Bouton>
          )}
        </div>
      )}
    </div>
  );
}
